import {createEstablishedAhpSessionRuntime} from './established-session'
import type {
  EstablishRequest,
  EstablishedAhpBootstrapState,
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionBootstrapAdapter,
  EstablishedAhpSessionError,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpResumptionAttempt,
  EstablishedAhpPlanningPolicy,
  FirstTurnHandoff,
  StopOutcome,
} from './contracts'
import {EstablishedAhpSessionError as SessionError} from './contracts'
import {relayFailureDisposition, relayFailureFromError} from '@github-ui/ahp-relay/relay-failure'
import {createEstablishedAhpResumptionDeadline} from './resumption-deadline'

function firstTurnFor(request: EstablishRequest): FirstTurnHandoff | undefined {
  if (request.mode === 'provision') return request.input.firstTurn
  if (request.mode === 'recover' || request.mode === 'resume-provision') return request.firstTurn
  return undefined
}

interface PendingFirstTurn {
  readonly handoff: FirstTurnHandoff
  cancellationRequested: boolean
  cancellationSent: boolean
  cancellationFinished: boolean
  cancellationRetried: boolean
  completed: boolean
  turnId?: string
  unsubscribeCancellation?: () => void
  cancellationPromise?: Promise<void>
  resolveCancellation?: () => void
}

function stateForRequest(request: EstablishRequest): EstablishedAhpBootstrapState {
  switch (request.mode) {
    case 'provision':
      return 'provisioning'
    case 'wake':
      return 'waking'
    case 'recover':
    case 'resume-provision':
      return 'reconnecting'
    case 'attach':
      return 'connecting'
  }
}

function asSessionError(
  error: unknown,
  state: EstablishedAhpBootstrapState,
  operation = 'establish',
): EstablishedAhpSessionError {
  if (error instanceof SessionError) return error
  const failure = relayFailureFromError(error)
  return new SessionError(
    operation,
    state,
    error instanceof Error ? error.message : 'Unable to establish AHP session.',
    relayFailureDisposition(failure).manualRetry,
    {cause: error, failure},
  )
}

/**
 * Builds a product-neutral bootstrap coordinator. Product packages supply the
 * provision/attach/wake/recover adapters; the established session runtime and
 * public session object are shared.
 */
export function createEstablishedAhpSessionBootstrap(
  adapter: EstablishedAhpSessionBootstrapAdapter,
  planning?: EstablishedAhpPlanningPolicy,
): EstablishedAhpSessionBootstrap {
  let state: EstablishedAhpBootstrapState = 'unbound'
  let session: EstablishedAhpSession | undefined
  let provisionedSession: EstablishedAhpSessionProvisioned | undefined
  let runtime: Awaited<ReturnType<typeof createEstablishedAhpSessionRuntime>> | undefined
  let disposed = false
  let generation = 0
  let establishing: Promise<EstablishedAhpSession> | undefined
  let establishingGeneration: number | undefined
  let firstTurnWork: Promise<EstablishedAhpSession> | undefined
  let firstTurnGeneration: number | undefined
  let firstTurnCompleted = false
  let invalidateFirstTurn: ((error: EstablishedAhpSessionError) => void) | undefined
  let firstTurnQueue: PendingFirstTurn[] = []
  let activeResumptionAttempt: EstablishedAhpResumptionAttempt | undefined

  const setState = (next: EstablishedAhpBootstrapState) => {
    state = next
  }

  const cleanupFirstTurnQueue = () => {
    if (!firstTurnCompleted || firstTurnQueue.some(item => item.unsubscribeCancellation)) return
    firstTurnQueue = []
  }

  const finishFirstTurnCancellation = (item: PendingFirstTurn) => {
    item.cancellationFinished = true
    item.unsubscribeCancellation?.()
    item.unsubscribeCancellation = undefined
    item.resolveCancellation?.()
    item.resolveCancellation = undefined
    item.cancellationPromise = undefined
    cleanupFirstTurnQueue()
  }

  const clearFirstTurnQueue = () => {
    for (const item of firstTurnQueue) finishFirstTurnCancellation(item)
    firstTurnQueue = []
  }

  const cancellationPromiseFor = (item: PendingFirstTurn): Promise<void> => {
    if (!item.cancellationRequested || item.cancellationSent || item.cancellationFinished) return Promise.resolve()
    if (!item.cancellationPromise) {
      item.cancellationPromise = new Promise(resolve => {
        item.resolveCancellation = resolve
      })
    }
    return item.cancellationPromise
  }

  const attemptFirstTurnCancellation = (
    item: PendingFirstTurn,
    establishedSession: EstablishedAhpSession,
    retry = false,
  ) => {
    if (!item.cancellationRequested || item.cancellationSent || item.cancellationFinished || !item.turnId) return

    const lifecycleState = establishedSession.lifecycle.getState()
    if (lifecycleState === 'connecting' || lifecycleState === 'reconnecting') {
      if (item.cancellationRetried) {
        finishFirstTurnCancellation(item)
        return
      }
      if (item.unsubscribeCancellation) return

      item.unsubscribeCancellation = establishedSession.lifecycle.subscribe(() => {
        const nextState = establishedSession.lifecycle.getState()
        if (nextState === 'live') {
          item.unsubscribeCancellation?.()
          item.unsubscribeCancellation = undefined
          attemptFirstTurnCancellation(item, establishedSession, true)
        } else if (nextState === 'failed' || nextState === 'disposed') {
          finishFirstTurnCancellation(item)
        }
      })
      return
    }
    if (lifecycleState !== 'live') {
      finishFirstTurnCancellation(item)
      return
    }

    const firstTurnSettled =
      establishedSession.view.getSnapshot().state?.turns.some(turn => turn.id === item.turnId) === true
    if (firstTurnSettled) {
      finishFirstTurnCancellation(item)
      return
    }

    if (retry) item.cancellationRetried = true

    const outcome = establishedSession.commands.cancel(item.turnId)
    if (outcome.accepted) {
      item.cancellationSent = true
      finishFirstTurnCancellation(item)
    } else {
      const nextLifecycleState = establishedSession.lifecycle.getState()
      if (nextLifecycleState === 'connecting' || nextLifecycleState === 'reconnecting') {
        attemptFirstTurnCancellation(item, establishedSession)
      } else {
        finishFirstTurnCancellation(item)
      }
    }
  }

  const establishAttempt = async (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    const establishingState = stateForRequest(request)
    const attemptGeneration = ++generation
    const resumptionDeadline =
      request.mode === 'attach' || request.mode === 'wake' || request.mode === 'recover'
        ? (request.resumptionAttempt ?? createEstablishedAhpResumptionDeadline())
        : undefined
    activeResumptionAttempt = resumptionDeadline
    const waitForResumption = async <T>(work: () => Promise<T>) => {
      if (!resumptionDeadline) return work()
      const signal = resumptionDeadline.signal
      if (signal.aborted) throw signal.reason
      return resumptionDeadline.waitFor(work())
    }
    let attemptRuntime: Awaited<ReturnType<typeof createEstablishedAhpSessionRuntime>> | undefined

    const ensureCurrent = () => {
      if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
      if (attemptGeneration !== generation) {
        throw new SessionError('establish', establishingState, 'AHP bootstrap attempt was superseded.', true)
      }
    }

    try {
      if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
      if (session && state === 'established') return session
      setState(establishingState)

      const provisioned =
        request.mode === 'provision'
          ? await adapter.provision(request.input)
          : request.mode === 'resume-provision'
            ? provisionedSession
            : request.mode === 'attach'
              ? await waitForResumption(() => adapter.attach(request.identity))
              : request.mode === 'wake'
                ? await waitForResumption(() => adapter.wake(request.identity))
                : await waitForResumption(() => adapter.recover(request.identity))

      ensureCurrent()
      if (!provisioned) {
        throw new SessionError(
          'establish',
          establishingState,
          'No provisioned AHP session is available to resume.',
          false,
        )
      }
      if (request.mode === 'provision') {
        provisionedSession = provisioned
        request.onIdentityResolved?.(provisioned.identity)
      }
      attemptRuntime = await createEstablishedAhpSessionRuntime({
        ...provisioned,
        ...(planning ? {planning} : {}),
        ...(resumptionDeadline ? {resumptionDeadline} : {}),
      })
      ensureCurrent()
      runtime = attemptRuntime
      await attemptRuntime.start()
      ensureCurrent()

      const establishedSession = attemptRuntime.session
      if (!firstTurnFor(request)) {
        attemptRuntime.activateResumptionRecovery()
        session = establishedSession
        setState('established')
      }
      return establishedSession
    } catch (error) {
      attemptRuntime?.dispose()
      if (attemptGeneration === generation && !disposed) {
        if (runtime === attemptRuntime) runtime = undefined
        session = undefined
        setState('failed')
      }
      throw asSessionError(error, state)
    } finally {
      resumptionDeadline?.dispose()
      if (activeResumptionAttempt === resumptionDeadline) activeResumptionAttempt = undefined
      if (establishingGeneration === attemptGeneration) {
        establishing = undefined
        establishingGeneration = undefined
      }
    }
  }

  const establishRuntime = (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    if (disposed) return Promise.reject(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
    if (firstTurnWork) return firstTurnWork
    if (session && state === 'established') return Promise.resolve(session)
    if (establishing) return establishing

    const attempt = establishAttempt(request)
    establishingGeneration = generation
    establishing = attempt
    return attempt
  }

  const dispatchFirstTurn = (
    firstTurn: FirstTurnHandoff,
    established: Promise<EstablishedAhpSession>,
  ): Promise<EstablishedAhpSession> => {
    if (firstTurnCompleted && session && state === 'established') return Promise.resolve(session)
    const existingItem = firstTurnQueue.find(item => item.handoff.requestId === firstTurn.requestId)
    if (!existingItem && (!firstTurnWork || firstTurn.origin === 'recovery')) {
      firstTurnQueue.push({
        handoff: firstTurn,
        cancellationRequested: false,
        cancellationSent: false,
        cancellationFinished: false,
        cancellationRetried: false,
        completed: false,
      })
    }
    if (firstTurnWork) return firstTurnWork

    const attemptGeneration = generation
    const attempt = (async () => {
      let dispatchStarted = false
      try {
        const establishedSession = await established
        if (disposed) throw new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false)
        if (attemptGeneration !== generation) {
          throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
        }
        const activeRuntime = runtime
        if (!activeRuntime) {
          throw new SessionError('establish', state, 'AHP session runtime is unavailable.', true)
        }

        const invalidated = Promise.withResolvers<never>()
        invalidateFirstTurn = invalidated.reject
        for (let index = 0; index < firstTurnQueue.length; index++) {
          const item = firstTurnQueue[index]
          if (!item) break
          let prompt = item.handoff.prompt
          if (item.handoff.configureSession) {
            await item.handoff.configureSession(establishedSession, prompt)
            if (attemptGeneration !== generation) {
              throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
            }
          }
          if (item.handoff.preparePrompt) {
            prompt = await item.handoff.preparePrompt(establishedSession, prompt)
            if (attemptGeneration !== generation) {
              throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
            }
          }
          dispatchStarted = true
          const receipt = await activeRuntime.session.input.submit(
            prompt,
            item.handoff.requestId,
            item.handoff.beforeDispatch,
            item.handoff.directTurnTelemetryContext,
          )
          if (attemptGeneration !== generation) {
            throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
          }
          item.turnId = receipt.turnId
          // Runs unguarded, like the direct-turn telemetry observers, so a broken
          // observer is visible rather than silently dropped. `dispatchStarted` is
          // already true above, so a throw here is still classified as a submit
          // failure and cannot make this dispatched prompt look safe to retry.
          item.handoff.afterDispatch?.(receipt)
          if (receipt.turnId) {
            try {
              item.handoff.onTurnDispatched?.(receipt.turnId)
            } catch (error) {
              // Dispatch already succeeded, so a best-effort observer must not make the turn retriable.
              // eslint-disable-next-line no-console
              console.error('AHP first-turn onTurnDispatched observer threw', error)
            }
          }
          attemptFirstTurnCancellation(item, establishedSession)
          await Promise.race([activeRuntime.waitForAccepted(receipt), invalidated.promise])
          if (attemptGeneration !== generation) {
            throw new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true)
          }
          item.completed = true
          if (item.cancellationRequested && index + 1 < firstTurnQueue.length) {
            await Promise.race([cancellationPromiseFor(item), invalidated.promise])
          }
        }

        session = establishedSession
        setState('established')
        firstTurnCompleted = true
        activeRuntime.activateResumptionRecovery()
        return establishedSession
      } catch (error) {
        if (attemptGeneration === generation && !disposed) {
          generation++
          runtime?.dispose()
          runtime = undefined
          session = undefined
          setState('failed')
        }
        throw asSessionError(error, state, dispatchStarted ? 'submit' : 'establish')
      } finally {
        if (firstTurnGeneration === attemptGeneration) {
          const cancellationPendingLiveRetry =
            firstTurnCompleted &&
            firstTurnQueue.some(
              item => item.cancellationRequested && !item.cancellationSent && Boolean(item.unsubscribeCancellation),
            )
          if (!cancellationPendingLiveRetry) clearFirstTurnQueue()
          invalidateFirstTurn = undefined
          firstTurnWork = undefined
          firstTurnGeneration = undefined
        }
      }
    })()

    firstTurnGeneration = attemptGeneration
    firstTurnWork = attempt
    return attempt
  }

  const establish = (request: EstablishRequest): Promise<EstablishedAhpSession> => {
    const established = establishRuntime(request)
    const firstTurn = firstTurnFor(request)
    return firstTurn ? dispatchFirstTurn(firstTurn, established) : established
  }

  const cancelPendingFirstTurn = (): StopOutcome => {
    if (disposed || !firstTurnWork || firstTurnCompleted) return {accepted: false}
    const pendingTurn = firstTurnQueue.findLast(item => !item.completed && !item.cancellationRequested)
    if (!pendingTurn) return {accepted: false}
    pendingTurn.cancellationRequested = true
    void cancellationPromiseFor(pendingTurn)
    if (runtime) attemptFirstTurnCancellation(pendingTurn, runtime.session)
    return {
      accepted: true,
      ...(pendingTurn.turnId ? {turnId: pendingTurn.turnId} : {}),
    }
  }

  return {
    get state() {
      return state
    },
    get session() {
      return session
    },
    get provisionedIdentity() {
      return provisionedSession?.identity
    },
    establish,
    cancelPendingFirstTurn,
    reset: () => {
      if (disposed) return
      activeResumptionAttempt?.cancel(
        new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true),
      )
      activeResumptionAttempt = undefined
      invalidateFirstTurn?.(new SessionError('establish', state, 'AHP bootstrap attempt was superseded.', true))
      generation++
      establishing = undefined
      establishingGeneration = undefined
      firstTurnWork = undefined
      firstTurnGeneration = undefined
      firstTurnCompleted = false
      invalidateFirstTurn = undefined
      clearFirstTurnQueue()
      runtime?.reset()
      runtime = undefined
      session = undefined
      provisionedSession = undefined
      adapter.reset?.()
      setState('unbound')
    },
    dispose: () => {
      if (disposed) return
      disposed = true
      activeResumptionAttempt?.cancel(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
      activeResumptionAttempt = undefined
      invalidateFirstTurn?.(new SessionError('establish', 'disposed', 'AHP bootstrap is disposed.', false))
      generation++
      establishing = undefined
      establishingGeneration = undefined
      firstTurnWork = undefined
      firstTurnGeneration = undefined
      firstTurnCompleted = false
      invalidateFirstTurn = undefined
      clearFirstTurnQueue()
      runtime?.dispose()
      runtime = undefined
      session = undefined
      provisionedSession = undefined
      adapter.dispose?.()
      setState('disposed')
    },
  }
}
